import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { FaPlay, FaPause, FaVolumeUp } from "react-icons/fa";
import "./TranscriptPlayer.css";
import {
  parseTranscript,
  parseMapping,
  speakerRole,
  speakerDisplayName,
} from "../../Helper/parseTranscript";

const speedOptions = [0.5, 1, 1.25, 1.5, 2];

// How long a manual scroll pauses the auto-follow. Long enough to read around,
// short enough that the view finds its way back without a button.
const AUTOSCROLL_RESUME_MS = 4000;

// "3:03", not "03:03" — the compact player's style, and what the transcript
// timestamps use, so every clock in the panel reads the same way.
const clock = (seconds) => {
  const s = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/**
 * Expanded audio player with a synchronised transcript.
 *
 * Opened from the Audio row's "Listen with transcript" button; the row itself
 * (label, open/close control) belongs to the surrounding panel, so this is
 * just the controls and the transcript. The transcript is the diarised text
 * stored for the call, shown as one card per turn: clicking a turn seeks the
 * audio to it, and the turn being spoken is highlighted and kept in view.
 * Recent calls carry real per-turn timestamps, so the sync is exact; older
 * ones don't, and their times are estimated and labelled as such rather than
 * presented as precise.
 *
 * Copying the transcript is blocked at the UI level (selection, clipboard and
 * context menu) — call transcripts are member conversations and are shown for
 * review, not for export.
 */
const TranscriptPlayer = ({
  src,
  transcript,
  mapping,
  transcriptStatus, // "loading" | "ready" | "error"
  autoPlay = false,
  // Resume point handed over from the compact bar, so "Listen with
  // transcript" continues from where the listener already was.
  initialTime = 0,
  // Reports playback position upward (throttled to the element's own
  // timeupdate cadence) so the parent can hold that resume point.
  onProgress,
  // Names of the agent(s) on the call, so a generic "Agent" turn can carry the
  // agent's own name when there is exactly one.
  agentNames = [],
}) => {
  const audioRef = useRef(null);
  const listRef = useRef(null);
  const itemRefs = useRef([]);
  const autoScrollRef = useRef(true);
  const resumeTimerRef = useRef(null);
  // The last turn the list was scrolled to, so ordinary playback only scrolls
  // when the turn actually changes — not on every timeupdate tick.
  const lastScrolledRef = useRef(-1);
  // Set by the audio element's `seeked` event. An explicit seek — dragging the
  // bar, clicking a turn — is the user saying "take me there", so it re-enables
  // auto-follow and scrolls even if the turn index happens not to change.
  const forceScrollRef = useRef(false);
  const activeIdxRef = useRef(-1);
  // Frozen at mount: later parent renders must not re-seek a live session.
  const initialTimeRef = useRef(initialTime);
  // The latest callback without re-subscribing the media listeners.
  const onProgressRef = useRef(onProgress);
  useEffect(() => {
    onProgressRef.current = onProgress;
  }, [onProgress]);

  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  // Slider position while the user is dragging it, so playback doesn't fight
  // the thumb under their finger. null = not dragging, follow the audio.
  const [dragPercent, setDragPercent] = useState(null);

  const { segments, timed } = useMemo(
    () => parseTranscript(transcript, parseMapping(mapping), duration),
    [transcript, mapping, duration],
  );

  // Role and display name per turn, computed once per transcript rather than
  // per render of every card.
  const turns = useMemo(
    () =>
      segments.map((seg) => {
        const role = speakerRole(seg.speaker, agentNames);
        return {
          ...seg,
          role,
          name: speakerDisplayName(seg.speaker, role, agentNames),
        };
      }),
    [segments, agentNames],
  );

  // The turn being spoken: the last segment that has started. Segments are in
  // time order, so a reverse scan finds it without needing exact end times.
  const activeIdx = useMemo(() => {
    if (!segments.length) return -1;
    for (let i = segments.length - 1; i >= 0; i--) {
      if (segments[i].start != null && segments[i].start <= currentTime + 0.01)
        return i;
    }
    return -1;
  }, [segments, currentTime]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    // An MP3's header duration is an estimate, and for some recordings the
    // element never volunteers a revision mid-play — the clock then sails
    // past the "total". The seekable range's end is the element's live
    // knowledge of how much audio actually exists, so the total is the best
    // of every signal, adopted monotonically as data loads ("progress").
    const bestDuration = () => {
      let d = Number.isFinite(audio.duration) ? audio.duration : 0;
      try {
        if (audio.seekable && audio.seekable.length > 0)
          d = Math.max(d, audio.seekable.end(audio.seekable.length - 1));
      } catch {
        /* a mid-load seekable range can throw; keep what we have */
      }
      return d;
    };
    const adoptDuration = () => {
      const d = bestDuration();
      if (d > 0) setDuration((prev) => Math.max(prev, d));
    };
    const onMeta = () => {
      adoptDuration();
      // Resume where the compact bar stood. Done here because a seek before
      // metadata is unreliable; the `seeked` this triggers also scrolls the
      // transcript to the resume point, even if playback stays paused.
      const resume = initialTimeRef.current;
      const d = bestDuration();
      if (resume > 0.5 && d > 0 && resume < d) {
        audio.currentTime = resume;
      }
    };
    const onTime = () => {
      setCurrentTime(audio.currentTime);
      onProgressRef.current?.(audio.currentTime);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    // The element fires this for every committed seek, whatever caused it —
    // the seek bar, a transcript click, keyboard on the slider. Reacting here
    // rather than in each control means no seek path can forget to resync the
    // transcript. It also fires while paused, which timeupdate does not.
    const onSeeked = () => {
      forceScrollRef.current = true;
      setCurrentTime(audio.currentTime);
      onProgressRef.current?.(audio.currentTime);
    };

    const onEnded = () => {
      onPause();
      // Playback has proven the real length; every estimate loses to it.
      if (Number.isFinite(audio.currentTime) && audio.currentTime > 0)
        setDuration(audio.currentTime);
    };

    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("durationchange", adoptDuration);
    audio.addEventListener("progress", adoptDuration);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("seeked", onSeeked);
    return () => {
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("durationchange", adoptDuration);
      audio.removeEventListener("progress", adoptDuration);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("seeked", onSeeked);
    };
  }, []);

  // Opened by the "Listen with transcript" click, so playback starts on mount
  // — still inside that click's activation window, which is what allows it.
  // If a stricter policy blocks it anyway, the player simply sits paused.
  useEffect(() => {
    // Captured once: the cleanup must pause the element this effect saw, and
    // the resume timer is read at cleanup time, not captured stale.
    const audio = audioRef.current;
    if (autoPlay && audio) {
      audio.play().catch(() => {});
    }
    return () => {
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
      audio?.pause();
    };
    // mount/unmount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  // Scroll the transcript LIST — by its own geometry, never scrollIntoView.
  // scrollIntoView walks every scrollable ancestor, and this list sits inside
  // a panel that can itself scroll, so it sometimes moved the panel instead of
  // the transcript. Setting the list's own scrollTop cannot touch anything
  // else. The active turn is kept around a third from the top: enough context
  // above, most of the reading space below, where speech is heading.
  const scrollLineIntoView = useCallback((idx) => {
    const list = listRef.current;
    const el = itemRefs.current[idx];
    if (!list || !el) return;
    // The turn's position INSIDE the list, from live rects — never offsetTop.
    // offsetTop is measured from the nearest *positioned* ancestor, and this
    // list is not positioned, so offsetTop reached far above the list and
    // every auto-scroll landed a controls-card-and-headers distance past the
    // spoken turn (the "scrolls to the wrong position" bug). Rect arithmetic
    // is relative to the viewport on both sides, so the reference cannot
    // drift with CSS positioning changes.
    const topInList =
      el.getBoundingClientRect().top -
      list.getBoundingClientRect().top +
      list.scrollTop;
    const target = topInList - list.clientHeight / 3;
    list.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
    lastScrolledRef.current = idx;
  }, []);

  useEffect(() => {
    activeIdxRef.current = activeIdx;
  }, [activeIdx]);

  // One follow rule, driven by time (so it also runs when a seek lands inside
  // the turn already highlighted): an explicit seek always re-engages
  // auto-follow and scrolls; ordinary playback scrolls only when the spoken
  // turn changes; a reader who scrolled away is left alone until follow
  // resumes.
  useEffect(() => {
    if (activeIdx < 0) return;
    const forced = forceScrollRef.current;
    if (forced) {
      forceScrollRef.current = false;
      autoScrollRef.current = true;
      if (resumeTimerRef.current) {
        clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = null;
      }
    }
    if (!autoScrollRef.current) return;
    if (!forced && lastScrolledRef.current === activeIdx) return;
    scrollLineIntoView(activeIdx);
  }, [currentTime, activeIdx, scrollLineIntoView]);

  const suspendAutoScroll = useCallback(() => {
    autoScrollRef.current = false;
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      autoScrollRef.current = true;
      // Resuming without moving looked like auto-follow had died — the
      // highlight was somewhere off-screen and nothing came back to it until
      // the next turn change. Snap back to the spoken turn on resume.
      scrollLineIntoView(activeIdxRef.current);
    }, AUTOSCROLL_RESUME_MS);
  }, [scrollLineIntoView]);

  const seekTo = useCallback((seconds, andPlay = false) => {
    const audio = audioRef.current;
    if (!audio || seconds == null) return;
    audio.currentTime = Math.max(0, seconds);
    setCurrentTime(audio.currentTime);
    if (andPlay) audio.play().catch(() => {});
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().catch(() => {});
    else audio.pause();
  };

  const blockClipboard = (e) => e.preventDefault();

  // The total holds STILL and the elapsed clock is clamped to it — two
  // numbers running together (67:41 / 67:41 …) reads as a broken timer. If
  // the file outlives every estimate, elapsed sits at the total for the last
  // stretch until `ended` corrects the total exactly. The raw currentTime
  // still drives the transcript logic unclamped.
  const shownTime =
    duration > 0 ? Math.min(currentTime, duration) : currentTime;
  const percent =
    dragPercent != null
      ? dragPercent
      : duration > 0
        ? Math.min(100, (shownTime / duration) * 100)
        : 0;

  return (
    <div className="tp-root">
      <audio ref={audioRef} src={src} preload="metadata" />

      <div className="tp-controls">
        <div className="tp-main-row">
          <button
            type="button"
            className={`tp-play ${isPlaying ? "is-pause" : "is-play"}`}
            title={isPlaying ? "Pause" : "Play"}
            aria-label={isPlaying ? "Pause" : "Play"}
            onClick={togglePlay}
          >
            {isPlaying ? <FaPause size={13} /> : <FaPlay size={13} />}
          </button>
          <FaVolumeUp size={14} className="tp-vol-icon" />
          <input
            type="range"
            className="tp-volume"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              setVolume(v);
              if (audioRef.current) audioRef.current.volume = v;
            }}
            aria-label="Volume"
          />
          <span className="tp-time">
            {clock(shownTime)} / {clock(duration)}
          </span>
        </div>

        {/* React fires onChange for every tick of a drag (not on release,
            unlike plain HTML), so this scrubs live: each tick seeks, the audio
            fires `seeked`, and the transcript follows the thumb. dragPercent
            keeps the thumb under the pointer between renders; releasing hands
            the slider back to playback. */}
        <input
          type="range"
          className="tp-seek"
          min="0"
          max="100"
          step="0.1"
          value={percent}
          onChange={(e) => {
            const p = parseFloat(e.target.value);
            if (Number.isNaN(p)) return;
            setDragPercent(p);
            if (duration > 0) seekTo((p / 100) * duration);
          }}
          onPointerUp={() => setDragPercent(null)}
          onKeyUp={() => setDragPercent(null)}
          onBlur={() => setDragPercent(null)}
          aria-label="Seek"
        />

        <div className="tp-speed-row" role="group" aria-label="Playback speed">
          <span className="tp-speed-label">Speed:</span>
          {speedOptions.map((s) => (
            <button
              key={s}
              type="button"
              className={`tp-speed-btn${s === playbackRate ? " is-active" : ""}`}
              aria-pressed={s === playbackRate}
              onClick={() => setPlaybackRate(s)}
            >
              {s}x
            </button>
          ))}
        </div>
      </div>

      <div className="tp-transcript-head">
        <span className="tp-transcript-title">Transcript</span>
        <span className="tp-transcript-meta">
          {segments.length > 0 && !timed ? (
            <span className="tp-estimated-note">timings estimated</span>
          ) : null}
        </span>
      </div>

      <div className="tp-disclaimer">
        This transcript is AI-generated and may contain inaccuracies in
        wording, speakers or timing.
      </div>

      <div
        className="tp-transcript"
        ref={listRef}
        onWheel={suspendAutoScroll}
        onTouchMove={suspendAutoScroll}
        onCopy={blockClipboard}
        onCut={blockClipboard}
        onContextMenu={blockClipboard}
        onDragStart={blockClipboard}
      >
        {transcriptStatus === "loading" ? (
          <div className="tp-empty">Loading transcript…</div>
        ) : transcriptStatus === "error" ? (
          <div className="tp-empty">Could not load the transcript.</div>
        ) : turns.length === 0 ? (
          <div className="tp-empty">No transcript available for this call.</div>
        ) : (
          turns.map((turn, i) => (
            <div
              key={i}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              role="button"
              tabIndex={0}
              className={`tp-turn tp-turn--${turn.role}${i === activeIdx ? " is-active" : ""}`}
              onClick={() => seekTo(turn.start, true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  seekTo(turn.start, true);
                }
              }}
              title={
                turn.start != null
                  ? `Play from ${clock(turn.start)}${timed ? "" : " (estimated)"}`
                  : undefined
              }
            >
              <div className="tp-turn-head">
                <span className="tp-turn-time">
                  {turn.start != null
                    ? `${timed ? "" : "~"}${clock(turn.start)}`
                    : "—"}
                </span>
                {turn.name ? (
                  <span className="tp-turn-speaker">{turn.name}</span>
                ) : null}
                {turn.tone ? (
                  <span className="tp-turn-tone" title="Tone detected by the analysis">
                    {turn.tone}
                  </span>
                ) : null}
              </div>
              <div className="tp-turn-text">{turn.text}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TranscriptPlayer;
